import { Checkbox, Flex, Input, Pagination, Typography } from 'antd';
import { FC, memo, useMemo } from 'react';

import { AwesomeNodePath } from '@/common/components';

import { PageNode } from '@/modules/Pages';

interface Props {
  pageItems: Pick<PageNode, 'id' | 'path'>[] | undefined;
  totalItems: number;
  currentPage: number;
  isPagesPathsLoading: boolean;
  search: string;
  disabled?: boolean;
  selectedIds: string[];
  onChangeSearch: (value: string) => void;
  onToggleNode: (node: Pick<PageNode, 'id' | 'path'>, checked: boolean) => void;
  onChangePage: (page: number) => void;
  onToggleSelectAllOnPage: (checked: boolean) => void;
}

export const RedirectsBulkPagesPicker: FC<Props> = memo(({
  pageItems,
  totalItems,
  currentPage,
  isPagesPathsLoading,
  search,
  onChangeSearch,
  onChangePage,
  selectedIds,
  onToggleNode,
  onToggleSelectAllOnPage,
  disabled,
}) => {
  const items = pageItems ?? [];

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const isAllOnPageSelected = items.length > 0 && items.every(({ id }) => selectedSet.has(id));
  const isSomeOnPageSelected = items.some(({ id }) => selectedSet.has(id));

  return (
    <Flex vertical gap={ 8 }>
      <Input
        value={ search }
        onChange={ (event) => onChangeSearch(event.target.value) }
        placeholder="Пошук серед сторінок сайту"
        allowClear
        disabled={ disabled || isPagesPathsLoading }
      />

      <Flex
        vertical
        gap={ 4 }
        style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: 8 }}
      >
        <Checkbox
          indeterminate={ isSomeOnPageSelected && !isAllOnPageSelected }
          checked={ isAllOnPageSelected }
          onChange={ (event) => onToggleSelectAllOnPage(event.target.checked) }
          disabled={ disabled || isPagesPathsLoading || items.length === 0 }
        >
          <Typography.Text strong>Вибрати всі (на сторінці)</Typography.Text>
        </Checkbox>

        { items.map((node) => (
          <Checkbox
            key={ node.id }
            checked={ selectedSet.has(node.id) }
            onChange={ (event) => onToggleNode(node, event.target.checked) }
            disabled={ disabled }
          >
            <AwesomeNodePath path={ node.path } style={{ maxWidth: '100%' }} ellipsis/>
          </Checkbox>
        )) }

        { !isPagesPathsLoading && items.length === 0 && (
          <Typography.Text type="secondary">Нічого не знайдено</Typography.Text>
        ) }
      </Flex>

      <Flex justify="flex-end">
        <Pagination
          size="small"
          current={ currentPage }
          total={ totalItems }
          onChange={ onChangePage }
          showSizeChanger={ false }
          disabled={ disabled || isPagesPathsLoading }
        />
      </Flex>
    </Flex>
  );
});

